import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories } from '@/mocks/categories';
import { shows } from '@/mocks/shows';
import { articles } from '@/mocks/articles';
import type { Show } from '@/mocks/shows';

function ShowCard({ show }: { show: Show }) {
  const formattedHeat =
    show.heat >= 10000 ? `${(show.heat / 10000).toFixed(0)}万` : show.heat;

  return (
    <Link
      to={`/category/${show.categorySlug}`}
      className="group cursor-pointer"
    >
      <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-3">
        <img
          src={show.cover}
          alt={show.title}
          title={`${show.title} - 极速影院免费综艺节目在线观看`}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        <div className="absolute top-2 right-2 bg-primary-500 text-white text-xs font-bold px-2 py-1 rounded-md whitespace-nowrap">
          {show.rating}
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
          <p className="text-white text-xs leading-relaxed line-clamp-2">
            {show.description}
          </p>
        </div>
      </div>
      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-1 mb-1">
        {show.title}
      </h3>
      <div className="flex items-center gap-2 text-xs text-foreground-500">
        <span className="whitespace-nowrap">{show.host}</span>
        <span className="text-foreground-300">·</span>
        <span className="flex items-center gap-1 whitespace-nowrap">
          <i className="ri-fire-line text-accent-500"></i>
          {formattedHeat}
        </span>
      </div>
    </Link>
  );
}

function ArticleCard({ article }: { article: typeof articles[0] }) {
  return (
    <Link
      to={`/article/${article.id}`}
      className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-background-100 hover:bg-background-200 transition-colors cursor-pointer"
    >
      <div className="w-full sm:w-48 h-32 flex-shrink-0 rounded-lg overflow-hidden">
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} - 极速影院综艺资讯`}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
        />
      </div>
      <div className="flex flex-col justify-between flex-1 min-w-0">
        <div>
          <h3 className="text-base font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
            {article.title}
          </h3>
          <p className="text-sm text-foreground-600 line-clamp-2 mb-2">
            {article.summary}
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs text-foreground-500 flex-wrap">
          <span className="bg-accent-100 text-accent-800 px-2 py-0.5 rounded-full whitespace-nowrap">
            {article.category}
          </span>
          <span className="whitespace-nowrap">{article.publishDate}</span>
          <span className="whitespace-nowrap">
            <i className="ri-eye-line mr-1"></i>
            {article.readCount >= 10000
              ? `${(article.readCount / 10000).toFixed(1)}万`
              : article.readCount}
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function HomePage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const hotShows = shows.filter((s) => s.heat > 9000000).slice(0, 4);
  const latestShows = [...shows]
    .sort((a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime())
    .slice(0, 8);
  const latestArticles = articles.slice(0, 3);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % 3);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const heroSlides = [
    {
      title: '极速影院',
      subtitle: '免费综艺节目在线观看',
      desc: '高清不缓冲，综艺大全畅享流畅娱乐体验',
      bg: "https://readdy.ai/api/search-image?query=Chinese%20variety%20show%20stage%20with%20colorful%20lights%20and%20grand%20performance%20atmosphere%2C%20warm%20red%20and%20gold%20tones%2C%20cinematic%20wide%20shot%2C%20entertainment%20spectacle%2C%20dramatic%20lighting%2C%20abstract%20artistic%20style&width=1920&height=900&seq=hero-bg-01&orientation=landscape",
    },
    {
      title: '热门综艺一网打尽',
      subtitle: '最新最全综艺节目',
      desc: '音乐、户外、美食、脱口秀，你想看的这里都有',
      bg: "https://readdy.ai/api/search-image?query=Chinese%20outdoor%20reality%20show%20scene%20with%20celebrities%20in%20beautiful%20nature%20landscape%2C%20warm%20sunset%20lighting%2C%20adventure%20atmosphere%2C%20cinematic%20wide%20shot%2C%20abstract%20artistic%20style%2C%20vibrant%20colors&width=1920&height=900&seq=hero-bg-02&orientation=landscape",
    },
    {
      title: '高清流畅不缓冲',
      subtitle: '极致观看体验',
      desc: '极速加载，高清画质，随时随地畅享综艺盛宴',
      bg: "https://readdy.ai/api/search-image?query=Chinese%20modern%20home%20theater%20setup%20with%20large%20screen%20displaying%20colorful%20entertainment%20show%2C%20cozy%20living%20room%2C%20warm%20ambient%20lighting%2C%20cinematic%20style%2C%20abstract%20artistic%20composition%2C%20high%20tech%20feel&width=1920&height=900&seq=hero-bg-03&orientation=landscape",
    },
  ];

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '极速影院',
    url: 'https://www.jisucinema.com',
    description: '极速影院免费综艺节目在线观看，高清不缓冲，综艺大全涵盖最新热门综艺，流畅娱乐体验。',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.jisucinema.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '极速影院',
    url: 'https://www.jisucinema.com',
    description: '极速影院是一个免费综艺节目在线观看平台，提供高清流畅的综艺娱乐内容。',
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative w-full h-[520px] md:h-[680px] overflow-hidden">
          {heroSlides.map((slide, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-700 ${
                currentSlide === index ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={slide.bg}
                alt={slide.subtitle}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/60"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center w-full px-4">
              <h1 className="font-heading text-3xl md:text-5xl text-white mb-3 transition-all duration-500">
                {heroSlides[currentSlide].title}
              </h1>
              <p className="text-lg md:text-xl text-white/90 mb-2 font-medium">
                {heroSlides[currentSlide].subtitle}
              </p>
              <p className="text-sm md:text-base text-white/70 mb-8">
                {heroSlides[currentSlide].desc}
              </p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <Link
                  to="/category/music"
                  className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-full font-semibold text-sm transition-all cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-circle-line mr-2"></i>
                  开始观看
                </Link>
                <Link
                  to="/about"
                  className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-full font-semibold text-sm transition-all cursor-pointer whitespace-nowrap backdrop-blur-sm"
                >
                  了解更多
                </Link>
              </div>
              <div className="flex items-center justify-center gap-2 mt-8">
                {heroSlides.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentSlide(index)}
                    className={`w-2.5 h-2.5 rounded-full transition-all cursor-pointer ${
                      currentSlide === index
                        ? 'bg-primary-500 w-6'
                        : 'bg-white/40 hover:bg-white/60'
                    }`}
                    aria-label={`幻灯片 ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">
                综艺分类
              </h2>
              <p className="text-sm text-foreground-600">
                选择你喜欢的综艺类型，发现更多精彩内容
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-3 p-5 rounded-lg bg-background-100 hover:bg-primary-50 transition-all cursor-pointer"
                >
                  <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-primary-100 group-hover:bg-primary-500 transition-colors">
                    <i
                      className={`${cat.icon} text-xl md:text-2xl text-primary-500 group-hover:text-white transition-colors`}
                    ></i>
                  </div>
                  <span className="text-sm font-semibold text-foreground-800 group-hover:text-primary-500 transition-colors whitespace-nowrap">
                    {cat.name}
                  </span>
                  <span className="text-xs text-foreground-500 text-center line-clamp-2">
                    {cat.description}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Shows */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-1">
                  热门推荐
                </h2>
                <p className="text-sm text-foreground-600">大家都在看的综艺节目</p>
              </div>
              <Link
                to="/category/competition"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer"
              >
                查看更多
                <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {hotShows.map((show) => (
                <ShowCard key={show.id} show={show} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Shows */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-1">
                  最新上线
                </h2>
                <p className="text-sm text-foreground-600">最新播出的精彩综艺节目</p>
              </div>
              <Link
                to="/category/lifestyle"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer"
              >
                查看更多
                <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {latestShows.map((show) => (
                <ShowCard key={show.id} show={show} />
              ))}
            </div>
          </div>
        </section>

        {/* Feature Banner */}
        <section className="w-full px-4 md:px-6 py-10">
          <div className="max-w-7xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden h-[200px] md:h-[280px]">
              <img
                src="https://readdy.ai/api/search-image?query=Chinese%20entertainment%20collage%20with%20multiple%20TV%20show%20posters%20arranged%20artistically%2C%20warm%20vibrant%20colors%2C%20abstract%20geometric%20composition%2C%20cinematic%20style%2C%20clean%20design%2C%20modern%20aesthetic&width=1600&height=560&seq=cta-banner&orientation=landscape"
                alt="综艺盛宴"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-black/60"></div>
              <div className="absolute inset-0 flex items-center justify-center text-center px-4">
                <div>
                  <h3 className="font-heading text-xl md:text-3xl text-white mb-3">
                    高清流畅，随时随地看综艺
                  </h3>
                  <p className="text-sm md:text-base text-white/80 mb-5">
                    覆盖全网热门综艺，极速加载不卡顿
                  </p>
                  <Link
                    to="/category/music"
                    className="inline-flex px-6 py-2.5 bg-accent-500 hover:bg-accent-600 text-white rounded-full font-semibold text-sm transition-all cursor-pointer whitespace-nowrap"
                  >
                    立即探索
                    <i className="ri-arrow-right-line ml-2"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Articles */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-1">
                  综艺资讯
                </h2>
                <p className="text-sm text-foreground-600">最新综艺资讯和深度分析</p>
              </div>
              <Link
                to="/article/1"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer"
              >
                更多文章
                <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="space-y-4">
              {latestArticles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}